import React, { useEffect, useRef, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Label, Input } from "reactstrap";
import { API_URL } from "../../../config";

const CATEGORY_ENDPOINT = {
  modules: "/distributor/purchase/modules",
  sensors: "/distributor/purchase/sensor",
  warranty: "/distributor/purchase/warranty",
};

const BASE_CATEGORIES = [
  { id: "modules", label: "Modules", price: 110 },
  { id: "sensors", label: "CapnoSensor™ (pack of 5)", price: 235 },
  { id: "warranty", label: "Extended Warranty 2 yrs & damage", price: 100 },
];

const PurchaseModule = ({ isOpen, toggle }) => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [purchaseForm, setPurchaseForm] = useState({ quantity: "" });
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [purchaseSuccess, setPurchaseSuccess] = useState(false);
  const [stock, setStock] = useState({ modules: { available: 0 }, sensor: { available: 0 }, warranty: { available: 0 } });
  const [prices, setPrices] = useState({ module: 110, sensor: 235, warranty: 100 });
  const paypalButtonRef = useRef();

  const userId = localStorage.getItem("user_id");
  const token = localStorage.getItem("accessToken");

  const categories = BASE_CATEGORIES.map((c) => ({
    ...c,
    price:
      c.id === "modules"
        ? prices.module
        : c.id === "sensors"
        ? prices.sensor
        : prices.warranty,
    available:
      c.id === "modules"
        ? stock.modules?.available || 0
        : c.id === "sensors"
        ? stock.sensor?.available || 0
        : stock.warranty?.available || 0,
  }));

  useEffect(() => {
    if (isOpen && userId) {
      fetch(`${API_URL}/distributor/purchase/stock/${userId}`, {
        headers: { "x-access-token": token },
      })
        .then((r) => r.json())
        .then((data) => { if (data.status) setStock(data.stock); })
        .catch((err) => console.error("Error fetching stock:", err));

      fetch(`${API_URL}/distributor/purchase/prices/${userId}`, {
        headers: { "x-access-token": token },
      })
        .then((r) => r.json())
        .then((data) => { if (data.status) setPrices(data.module_and_accessories); })
        .catch((err) => console.error("Error fetching prices:", err));
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && purchaseForm.quantity && selectedCategory && window.paypal) {
      const categoryData = categories.find((c) => c.id === selectedCategory);
      const totalPrice = parseInt(purchaseForm.quantity) * categoryData.price;

      if (paypalButtonRef.current) {
        paypalButtonRef.current.innerHTML = "";
      }

      window.paypal.Buttons({
        createOrder: (data, actions) => {
          return actions.order.create({
            purchase_units: [
              {
                amount: { value: totalPrice.toString() },
                description: `Purchase ${purchaseForm.quantity} ${categoryData.label}(s) @ $${categoryData.price} each`,
              },
            ],
          });
        },
        onApprove: async (data, actions) => {
          setPurchaseLoading(true);
          try {
            const details = await actions.order.capture();

            await fetch(`${API_URL}${CATEGORY_ENDPOINT[selectedCategory]}`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "x-access-token": token,
              },
              body: JSON.stringify({
                distributor_id: userId,
                transaction_id: details.id,
                transaction_body: JSON.stringify(details),
                quantity: parseInt(purchaseForm.quantity),
                price_per_item: categoryData.price,
                total_price: totalPrice,
              }),
            });

            setPurchaseSuccess(true);
            setPurchaseLoading(false);
            setTimeout(() => {
              handleClose();
            }, 2000);
          } catch (error) {
            setPurchaseLoading(false);
            console.error("Error:", error);
            alert("Error processing payment");
          }
        },
        onError: (err) => {
          console.error("PayPal error:", err);
          alert("Payment failed. Please try again.");
        },
      }).render(paypalButtonRef.current);
    }
  }, [isOpen, purchaseForm.quantity, selectedCategory]);

  const handleClose = () => {
    setPurchaseForm({ quantity: "" });
    setPurchaseSuccess(false);
    setSelectedCategory(null);
    toggle();
  };

  const handleBackToCategories = () => {
    setPurchaseForm({ quantity: "" });
    setPurchaseSuccess(false);
    setSelectedCategory(null);
  };

  const getSelectedCategoryData = () => {
    return categories.find((c) => c.id === selectedCategory);
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered className="modern-modal">
      <ModalHeader toggle={handleClose} className="modal-header-custom">
        <i className="fas fa-shopping-bag"></i> Purchase Modules & Accessories
      </ModalHeader>
      <ModalBody className="modal-body-custom">
        {purchaseSuccess ? (
          <div className="success-state">
            <div className="success-icon">
              <i className="fas fa-check-circle"></i>
            </div>
            <h3>Payment Successful!</h3>
            <p>Your purchase has been completed. Thank you for your order!</p>
          </div>
        ) : !selectedCategory ? (
          <div>
            <p style={{ marginBottom: "20px", color: "#666" }}>
              Select a product category to purchase:
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {categories.map((category) => (
                <div
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "16px",
                    border: "2px solid #e3e3e3",
                    borderRadius: "8px",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    backgroundColor: "white",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "rgb(77, 0, 77)";
                    e.currentTarget.style.backgroundColor = "#f5f0ff";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#e3e3e3";
                    e.currentTarget.style.backgroundColor = "white";
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <i
                      className={`fas fa-${
                        category.id === "modules"
                          ? "cubes"
                          : category.id === "sensors"
                          ? "microchip"
                          : "shield-alt"
                      }`}
                      style={{ fontSize: "20px", color: "rgb(77, 0, 77)" }}
                    ></i>
                    <div>
                      <p style={{ margin: "0", fontWeight: "600", fontSize: "16px" }}>
                        {category.label}
                      </p>
                      <p style={{ margin: "4px 0 0 0", fontSize: "12px", color: "#999" }}>
                        ${category.price} each
                      </p>
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <p style={{ margin: "0", fontWeight: "700", fontSize: "18px", color: "rgb(77, 0, 77)" }}>
                      {category.available}
                    </p>
                    <p style={{ margin: "4px 0 0 0", fontSize: "12px", color: "#999" }}>
                      Available
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <>
            <div
              style={{
                padding: "12px",
                backgroundColor: "#f5f0ff",
                borderRadius: "8px",
                marginBottom: "16px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <p style={{ margin: "0", fontWeight: "600" }}>
                  {getSelectedCategoryData()?.label}
                </p>
                <p style={{ margin: "4px 0 0 0", fontSize: "12px", color: "#666" }}>
                  ${getSelectedCategoryData()?.price} per item
                </p>
              </div>
              <button
                onClick={handleBackToCategories}
                style={{
                  background: "transparent",
                  border: "1px solid rgb(77, 0, 77)",
                  color: "rgb(77, 0, 77)",
                  padding: "6px 12px",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "12px",
                  fontWeight: "600",
                }}
              >
                ← Change
              </button>
            </div>

            <FormGroup>
              <Label className="form-label">
                <i className="fas fa-cubes"></i> Select Quantity
                {selectedCategory === "sensors" && (
                  <span style={{ fontSize: "12px", color: "#666", marginLeft: "8px" }}>
                    (Sold in packs of 5)
                  </span>
                )}
              </Label>

              {selectedCategory === "modules" ? (
                <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                  {[100, 200, 500, 1000].map((qty) => (
                    <button
                      key={qty}
                      type="button"
                      onClick={() =>
                        setPurchaseForm({ ...purchaseForm, quantity: qty.toString() })
                      }
                      style={{
                        flex: "1",
                        minWidth: "80px",
                        padding: "12px 16px",
                        border:
                          purchaseForm.quantity === qty.toString()
                            ? "2px solid rgb(77, 0, 77)"
                            : "2px solid #e3e3e3",
                        backgroundColor:
                          purchaseForm.quantity === qty.toString()
                            ? "#e3e3e3"
                            : "white",
                        borderRadius: "8px",
                        cursor: "pointer",
                        fontWeight:
                          purchaseForm.quantity === qty.toString() ? "700" : "600",
                        color:
                          purchaseForm.quantity === qty.toString()
                            ? "rgb(77, 0, 77)"
                            : "black",
                        transition: "all 0.3s ease",
                        fontSize: "14px",
                      }}
                      onMouseEnter={(e) => {
                        if (purchaseForm.quantity !== qty.toString()) {
                          e.target.style.borderColor = "rgb(77, 0, 77)";
                          e.target.style.backgroundColor = "#e3e3e3";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (purchaseForm.quantity !== qty.toString()) {
                          e.target.style.borderColor = "#e3e3e3";
                          e.target.style.backgroundColor = "white";
                        }
                      }}
                    >
                      {qty}
                    </button>
                  ))}
                </div>
              ) : (
                <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                  <Input
                    type="number"
                    min="1"
                    placeholder={
                      selectedCategory === "sensors"
                        ? "Enter number of packs (each pack = 5 units)"
                        : "Enter quantity"
                    }
                    value={purchaseForm.quantity}
                    onChange={(e) =>
                      setPurchaseForm({ ...purchaseForm, quantity: e.target.value })
                    }
                    style={{
                      flex: 1,
                      padding: "12px",
                      border: "2px solid #e3e3e3",
                      borderRadius: "8px",
                      fontSize: "14px",
                    }}
                  />
                  {selectedCategory === "sensors" && purchaseForm.quantity && (
                    <span
                      style={{
                        fontSize: "12px",
                        color: "#666",
                        whiteSpace: "nowrap",
                      }}
                    >
                      = {parseInt(purchaseForm.quantity) * 5} units
                    </span>
                  )}
                </div>
              )}
            </FormGroup>

            {/* Pricing Information */}
            <div
              style={{
                backgroundColor: "#e3e3e3",
                padding: "16px",
                borderRadius: "10px",
                marginBottom: "16px",
                border: "1px solid #e9d5ff",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "8px",
                }}
              >
                <span style={{ color: "black", fontWeight: "600" }}>
                  Price per {selectedCategory === "sensors" ? "Pack" : "Item"}:
                </span>
                <span style={{ color: "black", fontWeight: "600" }}>
                  ${getSelectedCategoryData()?.price}.00
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: "14px",
                  color: "black",
                  marginBottom: "8px",
                }}
              >
                <span>
                  Quantity: {purchaseForm.quantity || 0}
                  {selectedCategory === "sensors" && purchaseForm.quantity && (
                    <span style={{ color: "#666" }}>
                      {" "}
                      packs ({parseInt(purchaseForm.quantity) * 5} units)
                    </span>
                  )}
                </span>
                <span>
                  ${(
                    parseInt(purchaseForm.quantity || 0) *
                    (getSelectedCategoryData()?.price || 0)
                  ).toLocaleString("en-US")}
                  .00
                </span>
              </div>
              <hr style={{ borderColor: "black", width: "100%", margin: "8px 0" }} />
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: "16px",
                  fontWeight: "700",
                  color: "black",
                }}
              >
                <span>Total:</span>
                <span>
                  ${(
                    parseInt(purchaseForm.quantity || 0) *
                    (getSelectedCategoryData()?.price || 0)
                  ).toLocaleString("en-US")}
                  .00
                </span>
              </div>
            </div>

            {purchaseForm.quantity && (
              <div style={{ marginBottom: "16px", marginTop: "20px" }}>
                <div ref={paypalButtonRef}></div>
              </div>
            )}
          </>
        )}
      </ModalBody>
      {!purchaseSuccess && (
        <ModalFooter className="modal-footer-custom">
          <button
            onClick={selectedCategory ? handleBackToCategories : handleClose}
            style={{ background: "rgb(77, 0, 77)", color: "white" }}
            className="btn"
          >
            {selectedCategory ? "Back" : "Cancel"}
          </button>
        </ModalFooter>
      )}
    </Modal>
  );
};

export default PurchaseModule;
